import {
  AfriexCountryCode,
  AfriexCurrencyCode,
  AfriexTransactionChannel,
  AfriexTransactionProcessor,
  AfriexTransactionType,
} from '@/types';
import {BusinessTransactionType} from '@/types/AfriexBusinessTransactionType';
import {BusinessKYCDocumentType} from '@/types/BusinessKYCDocumentType';

type Branded<T, Brand extends string | number> = T & {
  readonly __brand: Brand;
};

export function createValidator<T, Brand extends string | number>(options: {
  validate: (value: T, meta?: Record<string, unknown>) => boolean;
  errorMessage: string;
}) {
  return {
    type: null as unknown as Branded<T, Brand>, // Type reference
    validate: (value: T, meta?: Record<string, unknown>): Branded<T, Brand> => {
      if (!options.validate(value, meta)) {
        throw new Error(options.errorMessage);
      }
      return value as Branded<T, Brand>;
    },
  };
}

export const ValidUserID = createValidator<string, 'UserID'>({
  validate: (id: string) => id.length > 0,
  errorMessage: 'Invalid user ID',
});

export const ValidURL = createValidator<string, 'URL'>({
  validate: (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (_) {
      return false;
    }
  },
  errorMessage: 'Invalid url',
});

export const ValidCurrencyCode = createValidator<
  AfriexCurrencyCode,
  'CurrencyCode'
>({
  validate: (currencyCode: AfriexCurrencyCode) =>
    Object.values(AfriexCurrencyCode).includes(currencyCode),
  errorMessage: 'Invalid currency code',
});

export const ValidCountryCode = createValidator<
  AfriexCountryCode,
  'CountryCode'
>({
  validate: (countryCode: AfriexCountryCode) =>
    Object.values(AfriexCountryCode).includes(countryCode),
  errorMessage: 'Invalid country code',
});

export const ValidTransactionChannel = createValidator<
  AfriexTransactionChannel,
  'AfriexTransactionChannel'
>({
  validate: (channel: AfriexTransactionChannel) =>
    Object.values(AfriexTransactionChannel).includes(channel),
  errorMessage: 'Invalid transaction channel',
});

export const ValidTransactionType = createValidator<
  AfriexTransactionType,
  'AfriexTransactionType'
>({
  validate: (channel: AfriexTransactionType | BusinessTransactionType) =>
    Object.values(AfriexTransactionType).includes(
      channel as AfriexTransactionType,
    ) ||
    Object.values(BusinessTransactionType).includes(
      channel as BusinessTransactionType,
    ),
  errorMessage: 'Invalid transaction type',
});

/**
 * Validates string is a valid Date
 * @note this does not account for leap years and daylight savings
 */
export const ValidIsoDateTime = createValidator<string, 'IsoDateTime'>({
  validate: (isoDateTime: string) => {
    try {
      if (!isoDateTime) {
        return false;
      }
      const dt = new Date(isoDateTime);
      if (isNaN(dt?.getTime())) {
        return false;
      }
      return true;
    } catch (_error: unknown) {
      return false;
    }
  },
  errorMessage: 'Invalid ISO date time',
});

export const ValidBVN = createValidator<string, 'BVN'>({
  validate: (bvn: string) => {
    if (!bvn) {
      return false;
    }
    if (bvn?.length !== 11) {
      return false;
    }
    if (!/^\d+$/.test(bvn)) {
      return false;
    }
    return true;
  },
  errorMessage: 'Invalid BVN',
});

export const ValidEmail = createValidator<string, 'Email'>({
  validate: (email: string) => {
    if (!email?.includes('@')) {
      return false;
    }
    if (email?.includes('..')) {
      return false;
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  },
  errorMessage: 'Invalid email',
});

export const ValidName = createValidator<string, 'Name'>({
  validate: (name: string) => {
    return name?.length >= 2;
  },
  errorMessage: 'Invalid name',
});

export const ValidPhone = createValidator<string, 'Phone'>({
  validate: (phone: string) => {
    return /^\+\d+$/.test(phone);
  },
  errorMessage: 'Invalid phone',
});

export const ValidKYCDocumentType = createValidator<string, 'KYCDocumentType'>({
  validate: (type: string) =>
    Object.values(BusinessKYCDocumentType).includes(
      type as BusinessKYCDocumentType,
    ),
  errorMessage: 'Invalid KYC document type',
});

export const ValidTransactionProcessor = createValidator<
  string,
  'AfriexTransactionProcessor'
>({
  validate: (type: string) =>
    Object.values(AfriexTransactionProcessor).includes(
      type as AfriexTransactionProcessor,
    ),
  errorMessage: 'Invalid transaction processor',
});
